import {
    faArrowRightFromBracket,
    faArrowRightToBracket,
    faBuilding,
    faClock,
    faHandshake,
    faMapLocationDot,
    faPencil,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    IonButton,
    IonContent,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonText,
    IonTextarea,
    ToastOptions,
    useIonToast,
} from '@ionic/react';
import { useSetAtom } from 'jotai';
import { each, filter, flow, map, reject, size } from 'lodash/fp';
import { DateTime } from 'luxon';
import React, { useEffect, useReducer, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Header } from '../components/Header';
import { RoundedSquareIcon } from '../components/Icon';
import { ItemWithHours } from '../components/Item';
import { Many2OneSelector } from '../components/Many2OneSelector';
import Timer from '../components/Timer';
import { adjustMadsPropertiesByTime, useEmployees, useMads, useWorkPlaces } from '../hooks/useSwr';
import { OdooPointing, pointingFields, PointingToSaved } from '../types/models';
import { dateTimeToOdooDatetime } from '../utils/date';
import { defaultToastOptions, loadingAtom, pointingToEditIdAtom } from '../utils/Global';
import odoo, { standardizedOdooPointing, saveOrCreate, unstandardizedOdooPointing } from '../utils/odoo';
import { cleanDataBeforeSending, getMetaData, pointingInitialValue, PointingState } from '../utils/odooUtils';
import { Pointing as PointingType } from '../types/models';
import { IosHeader } from '../components/IosHeader';

// TODO : Ajouter commentaire sur le composant, qui est-il ? a quoi sert-il ?
const Pointing: React.FC = () => {
    const history = useHistory();

    const [presentToast] = useIonToast();

    const setLoading = useSetAtom(loadingAtom);
    const setPointingToEditId = useSetAtom(pointingToEditIdAtom);
    const isLoaded = useRef(false);

    const { data: employees, ids: employeeIds } = useEmployees();
    const { data: mads, dataWithPlacehoder: madsPlaceholder } = useMads();
    const { data: workPlaces, dataWithPlacehoder: workPlacesPlaceholder } = useWorkPlaces();

    // TODO: Duplicate code with PointingDetail.tsx
    const [pointing, setPointing] = useReducer((prev: PointingType, next: Partial<PointingType>) => {
        let newPointing = { ...prev, ...next };

        // Auto assign employee if there is only one
        if (size(employees) === 1) {
            newPointing.employee_id = employees[0].id;
        }

        // get mads linked to the employee
        const activeMadsOfEmployee = filter(
            (m) => m.employee_id[0] === newPointing.employee_id && !m.disabled,
            adjustMadsPropertiesByTime(mads, DateTime.now())
        );

        // Auto assign mad if there is only one
        if (size(activeMadsOfEmployee) === 1 && newPointing.mad_id !== 0) {
            newPointing.mad_id = activeMadsOfEmployee[0].id;
        }

        // If no mad available, set mad_id to 0
        if (newPointing.employee_id >= 0 && size(activeMadsOfEmployee) === 0) {
            newPointing.mad_id = 0;
        }

        // get workPlaces linked to the mad
        const workPlacesOfMad = filter((w) => w.mad_ids.includes(newPointing.mad_id), workPlaces);

        // Auto assign workplace if there is only one
        if (size(workPlacesOfMad) === 1 && newPointing.workplace_id !== 0) {
            newPointing.workplace_id = workPlacesOfMad[0].id;
        }

        // If no workplace available, set workplace_id to 0
        if (newPointing.mad_id >= 0 && size(workPlacesOfMad) === 0) {
            newPointing.workplace_id = 0;
        }

        return newPointing;
    }, pointingInitialValue('', reject('disabled', adjustMadsPropertiesByTime(mads, DateTime.now())), workPlaces, employees));

    const title = 'Pointage';

    const canStartPointing =
        (pointing.mad_id > 0 || (pointing.mad_id === 0 && pointing.other_mad)) &&
        (pointing.workplace_id > 0 || (pointing.workplace_id === 0 && pointing.other_workplace)) &&
        !pointing.real_time_start &&
        (pointing.employee_id || pointing.mad_id);

    const canStopPointing =
        (pointing.mad_id > 0 || (pointing.mad_id === 0 && pointing.other_mad)) &&
        (pointing.workplace_id > 0 || (pointing.workplace_id === 0 && pointing.other_workplace)) &&
        pointing.real_time_start &&
        (pointing.employee_id || pointing.mad_id) &&
        pointing.id;

    useEffect(() => {
        setLoading(true);

        odoo.searchReadOne(
            'hr.madper.app.pointing.time',
            {
                fields: pointingFields,
                domain: [
                    ['employee_id', 'in', employeeIds],
                    ['user_employee_validation_date', '=', false],
                    ['user_adherent_id', '=', false],
                    ['origin_id', '=', false],
                    ['state', 'in', [PointingState.StartPointing, PointingState.EndPointing]],
                ],
                sort: 'create_date desc',
            },
            true
        )
            .then((employeePointing) => {
                if (employeePointing) {
                    const standardizedPointing = standardizedOdooPointing(employeePointing);

                    if (employeePointing.real_time_end) {
                        setPointingToEditId(standardizedPointing.id);
                        history.push('/pointage/confirmation');
                    }
                    setPointing(standardizedPointing);
                }
            })
            .catch((e) => {
                each((errorToast: ToastOptions) => presentToast(errorToast), e.messages);
            })
            .finally(() => {
                setLoading(false);
                isLoaded.current = true;
            });

        return () => {
            setLoading(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startPointing = async () => {
        setLoading(true);
        if (!size(employeeIds)) {
            presentToast({
                ...defaultToastOptions,
                message: "Aucun employé n'est associé à votre compte",
                color: 'danger',
                duration: 5000,
            });

            return;
        }

        const startDate = DateTime.now();

        try {
            const extraData = await getMetaData();

            const odooPointing = await saveOrCreate<PointingToSaved, OdooPointing>(
                'hr.madper.app.pointing.time',
                {
                    ...unstandardizedOdooPointing({
                        ...cleanDataBeforeSending(pointing),
                        metadata: { ...pointing.metadata, start: { ...pointing.metadata.start, ...extraData } },
                    }),
                    date: startDate.toISODate(),
                    real_time_start: dateTimeToOdooDatetime(startDate),
                },
                pointingFields,
                { forwardIp: true, pointingState: 'start' }
            );

            setPointing(standardizedOdooPointing(odooPointing[0]));
        } catch (e) {
            presentToast({
                ...defaultToastOptions,
                message: 'Une erreur est survenue lors de la création du pointage',
                color: 'danger',
                duration: 3000,
            });
        }
        setLoading(false);
    };

    const stopPointing = async () => {
        if (pointing.id) {
            setLoading(true);
            try {
                const extraData = await getMetaData();
                const res = await saveOrCreate<PointingToSaved, OdooPointing>(
                    'hr.madper.app.pointing.time',
                    {
                        ...unstandardizedOdooPointing({
                            ...pointing,
                            metadata: { ...pointing.metadata, end: { ...pointing.metadata.end, ...extraData } },
                        }),
                        real_time_end: dateTimeToOdooDatetime(DateTime.now()),
                    },
                    pointingFields,
                    { forwardIp: true, pointingState: 'end' }
                );

                setPointingToEditId(res[0].id);
                history.push('/pointage/confirmation');
            } catch (e) {
                presentToast({
                    ...defaultToastOptions,
                    message: 'Une erreur est survenue lors de la création du pointage',
                    color: 'danger',
                    duration: 3000,
                });
            }
            setLoading(false);
        }
    };

    if (!pointing) {
        return null;
    }

    return (
        <IonPage>
            <IosHeader title={title} />
            <IonContent fullscreen={true}>
                <Header title={title} />
                <div className="w-full flex justify-center py-8 text-2xl font-black text-slate-900">
                    <span>{DateTime.now().toLocaleString(DateTime.DATE_FULL)}</span>
                </div>
                <IonList lines="none">
                    <div className="pb-4">
                        <Many2OneSelector
                            items={adjustMadsPropertiesByTime(madsPlaceholder, DateTime.now())}
                            value={pointing.mad_id}
                            label="Client"
                            lines={pointing.mad_id === 0 ? 'none' : 'full'}
                            icon={faHandshake}
                            fieldToDisplay="display_name"
                            selectOptions={{
                                placeholder: 'Client',
                                interfaceOptions: {
                                    header: 'Client',
                                    subHeader: 'Sélectionnez votre client',
                                    translucent: true,
                                },
                            }}
                            onChange={(value) => {
                                setPointing({
                                    mad_id: value?.id,
                                    employee_id: value?.employee_id[0],
                                });
                            }}
                        />
                        {pointing.mad_id === 0 && (
                            <IonItem className="bg-white">
                                <div className="w-full">
                                    <hr className="w-60 h-[2px] mx-auto my-1 bg-gray-200 border-0 rounded md:my-5" />
                                    <IonTextarea
                                        onInput={(e: any) => {
                                            setPointing({
                                                other_mad: e.target.value,
                                            });
                                        }}
                                        aria-label="Autre MAD"
                                        placeholder="Si pas d'affaire, veuillez justifier"
                                        clearOnEdit={true}
                                        value={pointing.other_mad}
                                        autoGrow={true}
                                    />
                                </div>
                            </IonItem>
                        )}
                    </div>

                    {employees && size(employees) > 1 && pointing.mad_id === 0 && (
                        <div className="pb-4">
                            <Many2OneSelector
                                items={flow(
                                    // FIXME : What is that ? I don't understand what is suppose to do
                                    //TODO : Refactor this to company_id to select employee if is an employee or other if admin/geiq
                                    filter('company_id'),
                                    map((e: (typeof employees)[0]) => ({
                                        id: e.id,
                                        name: e.company_id[1],
                                    }))
                                )(employees)}
                                value={pointing.employee_id}
                                label="Société"
                                lines="full"
                                icon={faBuilding}
                                selectOptions={{
                                    placeholder: 'Société',
                                    interfaceOptions: {
                                        header: 'Société',
                                        subHeader: 'Sélectionnez votre société',
                                        translucent: true,
                                    },
                                }}
                                onChange={(value) => {
                                    setPointing({
                                        employee_id: value?.id,
                                    });
                                }}
                            />
                        </div>
                    )}

                    <div className="pb-4">
                        <Many2OneSelector
                            items={filter(
                                (wpp) => wpp.mad_ids.includes(pointing?.mad_id) || wpp.id === 0,
                                workPlacesPlaceholder
                            )}
                            value={pointing.workplace_id}
                            label="Chantier"
                            lines={pointing.workplace_id === 0 ? 'none' : 'full'}
                            icon={faMapLocationDot}
                            selectOptions={{
                                placeholder: 'Chantier',
                                interfaceOptions: {
                                    header: 'Chantier',
                                    subHeader: 'Sélectionnez votre chantier',
                                    translucent: true,
                                },
                            }}
                            onChange={(value) => {
                                if (value !== undefined) {
                                    setPointing({
                                        workplace_id: value?.id,
                                    });
                                }
                            }}
                        />
                        {pointing.workplace_id === 0 && (
                            <IonItem className="bg-white">
                                <div className="w-full">
                                    <hr className="w-60 h-[2px] mx-auto my-1 bg-gray-200 border-0 rounded md:my-5" />
                                    <IonTextarea
                                        placeholder="Veuillez indiquer l'adresse"
                                        clearOnEdit={true}
                                        value={pointing?.other_workplace || ''}
                                        aria-label="Autre chantier"
                                        onIonChange={(e) => {
                                            setPointing({
                                                other_workplace: e.detail.value || '',
                                            });
                                        }}
                                        autoGrow={true}
                                    />
                                </div>
                            </IonItem>
                        )}
                    </div>
                    <IonItem className="bg-white" lines="none">
                        <RoundedSquareIcon icon={faPencil} color={'bg-night'} />
                        <IonLabel className="pl-1">Note</IonLabel>
                    </IonItem>
                    <IonItem className="bg-white">
                        <div className="w-full">
                            <hr className="w-60 h-[2px] mx-auto my-1 bg-gray-200 border-0 rounded md:my-5" />
                            <IonTextarea
                                value={pointing.employee_note || ''}
                                placeholder="Tapez quelque chose ici"
                                aria-label="Note employé"
                                onIonInput={(e) => {
                                    setPointing({
                                        employee_note: e.target.value || '',
                                    });
                                }}
                                autoGrow={true}
                            />
                        </div>
                    </IonItem>
                </IonList>
                {pointing.real_time_start && (
                    <>
                        <IonLabel className="transition duration-500 ion-text-center m-1 text-slate-900 text-7xl font-extralight leading-10">
                            <Timer start={pointing.real_time_start} />
                        </IonLabel>

                        <ItemWithHours
                            icon={faClock}
                            title="Heure de début"
                            color="bg-amber-200"
                            value={pointing.real_time_start}
                        />
                    </>
                )}
                <div className="grid content-center bg-white mx-auto h-24 my-10 rounded-2xl w-3/4">
                    <div className="flex ion-text-center px-2">
                        <IonButton
                            color="success"
                            type="button"
                            className="w-2/4 h-20 font-black text-sm mr-6"
                            onClick={startPointing}
                            disabled={!canStartPointing}
                        >
                            <div className="flex flex-col items-center pt-1">
                                <FontAwesomeIcon icon={faArrowRightToBracket} className="fa-2x" />
                                <span className="font-medium text-[15px]">Début</span>
                            </div>
                        </IonButton>
                        <IonButton
                            color="danger"
                            className="w-2/4 h-20 font-black text-sm"
                            onClick={stopPointing}
                            disabled={!canStopPointing}
                        >
                            <div className="flex flex-col items-center pt-1">
                                <FontAwesomeIcon icon={faArrowRightFromBracket} className="fa-2x" />
                                <span className="font-medium text-[15px]">Fin</span>
                            </div>
                        </IonButton>
                    </div>
                </div>
            </IonContent>
        </IonPage>
    );
};

export const PointageTopSection: React.FC<{ date: string | undefined }> = (date) => {
    return (
        <div>
            <IonText className="text-2xl break-words ion-text-center font-bold text-black py-6">
                <div className="ion-text-center">
                    {(date.date ? DateTime.fromFormat(date.date, 'yyyy-MM-dd') : DateTime.now())
                        .setLocale('fr')
                        .toFormat('cccc dd MMMM')
                        .toUpperCase()}
                </div>
            </IonText>
        </div>
    );
};

export default Pointing;
